import React, { useEffect, useMemo, useState, useRef } from "react";
import {
    Container,
    Paper,
    Stack,
    Group,
    Title,
    Text,
    Select,
    TextInput,
    Button,
    Badge,
    Table,
    Loader,
    Center,
    Divider,
    ActionIcon,
    Tooltip,
    ScrollArea,
    Collapse,
    Pagination,
} from "@mantine/core";
import { DateInput } from "@mantine/dates";
import { showNotification } from "@mantine/notifications";
import {
    IconPlaneArrival,
    IconPlaneDeparture,
    IconRefresh,
    IconSettings,
    IconCircleCheck,
    IconClock,
    IconX,
    IconCircle,
    IconRadar,
    IconNote,
    IconBrandWhatsapp,
    IconFileSpreadsheet,
} from "@tabler/icons-react";
import { useParams } from "react-router-dom";
import { useDebouncedValue } from "@mantine/hooks";

import AssignBookingDrawer from "./AssignBookingDrawer";
import TransportMessageDrawer from "./../TransportMessageDrawer";
import {
    DriverPersonReadDto,
    TransportVehicleReadDto,
    AdminTransportBookingReadDto,
    AdminTransportBookingTotalsDto,
    FlightStatusDto,
} from "../../../Transport/types";
import {
    getAdminBookings,
    getAdminTotals,
    getVehicles,
    checkFlightStatus,
    getDriverLog
} from "../../../../api/transportAdminApi";
import * as XLSX from "xlsx";

const RED = "#ff5e5e";

function toErrorMessage(e: any): string {
    const data = e?.response?.data;
    if (!data) return "Kunne ikke hente bookings.";
    if (typeof data === "string") return data;

    if (typeof data === "object") {
        const title = data.title || "Fejl";
        const status = data.status ? ` (${data.status})` : "";
        if (data.errors && typeof data.errors === "object") {
            const lines = Object.entries(data.errors)
                .flatMap(([field, msgs]) =>
                    (Array.isArray(msgs) ? msgs : [String(msgs)]).map((m) => `${field}: ${m}`)
                )
                .slice(0, 6);
            return [title + status, ...lines].join("\n");
        }
        return title + status;
    }

    return String(data);
}

function kindToString(kind: number | null | undefined): string {
    switch (kind) {
        case 1:
            return "arrival";
        case 2:
            return "departure";
        case 3:
            return "local";
        default:
            return "";
    }
}

function kindMeta(kind: number | string | null | undefined) {
    const v = typeof kind === "number" ? kindToString(kind) : String(kind ?? "").toLowerCase();
    switch (v) {
        case "arrival":
            return { label: "Arrival", icon: <IconPlaneArrival size={16} /> };
        case "departure":
            return { label: "Departure", icon: <IconPlaneDeparture size={16} /> };
        case "local":
            return { label: "Local", icon: <span style={{ fontSize: 16 }}>🚗</span> };
        default:
            return { label: "Unknown", icon: <span style={{ fontSize: 16 }}>❓</span> };
    }
}

function isArrival(kind: any) {
    return kind === 1 || String(kind).toLowerCase() === "arrival";
}

function isDeparture(kind: any) {
    return kind === 2 || String(kind).toLowerCase() === "departure";
}

function fromToForRow(b: AdminTransportBookingReadDto) {
    const pickup = b.pickUpPlace ?? "-";
    const dropoff = b.dropOffPlace ?? "-";
    const flight = b.flightNo ? b.flightNo : "Flight";

    if (isArrival(b.kind)) return { fromText: flight, toText: dropoff };
    if (isDeparture(b.kind)) return { fromText: pickup, toText: flight };
    return { fromText: pickup, toText: dropoff };
}

function statusMetaFromString(status?: string | null) {
    const s = String(status ?? "").toLowerCase().trim();

    if (["confirmed", "confirm", "ok"].includes(s)) {
        return { label: "Confirmed", color: "green" as const, icon: <IconCircleCheck size={14} /> };
    }

    if (["delayed", "delay"].includes(s)) {
        return { label: "Delayed", color: "orange" as const, icon: <IconClock size={14} /> };
    }

    if (["cancelled", "canceled", "cancel"].includes(s)) {
        return { label: "Cancelled", color: "red" as const, icon: <IconX size={14} /> };
    }

    if (s) {
        return { label: status as string, color: "gray" as const, icon: <IconCircle size={14} /> };
    }

    return { label: "Booked", color: "gray" as const, icon: <IconCircle size={14} /> };
}

function statusMetaFromId(id?: number | null) {
    switch (id) {
        case 3:
            return { label: "Confirmed", color: "green" as const, icon: <IconCircleCheck size={14} /> };
        case 2:
            return { label: "Delayed", color: "orange" as const, icon: <IconClock size={14} /> };
        case 4:
            return { label: "Cancelled", color: "red" as const, icon: <IconX size={14} /> };
        default:
            return { label: "Booked", color: "gray" as const, icon: <IconCircle size={14} /> };
    }
}

function driverStatusMeta(statusId?: number | null) {
    switch (statusId) {
        case 1:
            return { label: "På vej", color: "blue" as const };
        case 2:
            return { label: "Ved pick", color: "yellow" as const };
        case 3:
            return { label: "Samlet op", color: "orange" as const };
        case 4:
            return { label: "Afleveret", color: "green" as const };
        default:
            return { label: "No driver update", color: "gray" as const };
    }
}

function flightStatusBadge(
    b: AdminTransportBookingReadDto,
    liveStatus?: FlightStatusDto
) {
    if (!b.flightNo) {
        return <Text size="xs" c="dimmed">No flight</Text>;
    }

    if (!liveStatus) {
        return (
            <Badge variant="light" color="gray" leftSection={<IconRadar size={14} />}>
                Ikke tjekket
            </Badge>
        );
    }

    const raw = String(liveStatus.status ?? "").toLowerCase().trim();

    let color: string = "gray";
    let label = liveStatus.status ?? "Ukendt";

    if (["scheduled", "on time", "ontime"].includes(raw)) {
        color = "blue";
        label = "On time";
    } else if (["boarding", "gate open"].includes(raw)) {
        color = "cyan";
        label = "Boarding";
    } else if (["departed", "arrived", "landed"].includes(raw)) {
        color = "green";
        label = "Completed";
    } else if (["delayed"].includes(raw)) {
        color = "orange";
        label = "Delayed";
    } else if (["cancelled", "canceled"].includes(raw)) {
        color = "red";
        label = "Cancelled";
    } else if (["not found", "no data"].includes(raw)) {
        color = "gray";
        label = "Ingen data";
    } else if (["invalid flight number", "missing flight number"].includes(raw)) {
        color = "gray";
        label = "Ugyldigt flightnr.";
    }

    const tooltipText = [
        `Flight: ${liveStatus.flightNo ?? b.flightNo}`,
        liveStatus.message ? `Besked: ${liveStatus.message}` : null,
        liveStatus.scheduledTime ? `Scheduled: ${liveStatus.scheduledTime}` : null,
        liveStatus.estimatedTime ? `Estimated: ${liveStatus.estimatedTime}` : null,
        liveStatus.actualTime ? `Actual: ${liveStatus.actualTime}` : null,
        liveStatus.terminal ? `Terminal: ${liveStatus.terminal}` : null,
        liveStatus.gate ? `Gate: ${liveStatus.gate}` : null,
    ]
        .filter(Boolean)
        .join("\n");

    return (
        <Tooltip label={tooltipText} multiline w={280}>
            <Badge variant="light" color={color} leftSection={<IconRadar size={14} />}>
                {label}
            </Badge>
        </Tooltip>
    );
}

function previewText(s?: string | null, max = 46) {
    const t = (s ?? "").trim();
    if (!t) return "";
    if (t.length <= max) return t;
    return t.slice(0, max - 1) + "…";
}

function compareValues(a: any, b: any) {
    if (a == null && b == null) return 0;
    if (a == null) return 1;
    if (b == null) return -1;

    if (typeof a === "number" && typeof b === "number") {
        return a - b;
    }

    return String(a).localeCompare(String(b), undefined, {
        numeric: true,
        sensitivity: "base",
    });
}

function timeToMinutes(value?: string | null): number | null {
    if (!value) return null;

    const match = String(value).trim().match(/^(\d{1,2}):(\d{2})/);
    if (!match) return null;

    const hours = Number(match[1]);
    const minutes = Number(match[2]);

    if (Number.isNaN(hours) || Number.isNaN(minutes)) return null;
    if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return null;

    return hours * 60 + minutes;
}

export default function AdminTransportBookings() {
    const { eventId } = useParams<{ eventId: string }>();
    const eventIdNum = eventId ? Number(eventId) : NaN;

    const [loading, setLoading] = useState(true);

    const [kind, setKind] = useState<string | null>(null);
    const [status, setStatus] = useState<string | null>(null);
    const [from, setFrom] = useState<Date | null>(null);
    const [to, setTo] = useState<Date | null>(null);
    const [q, setQ] = useState("");

    const [driverFilter, setDriverFilter] = useState<string | null>(null);
    const [vehicleFilter, setVehicleFilter] = useState<string | null>(null);
    const [planFilter, setPlanFilter] = useState<string>("all");

    const [bookings, setBookings] = useState<AdminTransportBookingReadDto[]>([]);
    const [totals, setTotals] = useState<AdminTransportBookingTotalsDto[]>([]);

    const [drivers] = useState<DriverPersonReadDto[]>([]);
    const [vehicles, setVehicles] = useState<TransportVehicleReadDto[]>([]);

    const [drawerOpen, setDrawerOpen] = useState(false);
    const [selected, setSelected] = useState<AdminTransportBookingReadDto | null>(null);

    const [messageDrawerOpen, setMessageDrawerOpen] = useState(false);
    const [messageBooking, setMessageBooking] = useState<AdminTransportBookingReadDto | null>(null);

    const [debouncedQ] = useDebouncedValue(q, 350);
    const bookingsRefreshRef = useRef<number | null>(null);

    const [selectedTotalDate, setSelectedTotalDate] = useState<string | null>(null);
    const [totalsOpen, setTotalsOpen] = useState(true);

    const [timeFrom, setTimeFrom] = useState("");
    const [timeTo, setTimeTo] = useState("");

    const [page, setPage] = useState(1);
    const PAGE_SIZE = 25;

    const [sortBy, setSortBy] = useState<string | null>(null);
    const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");

    const [checkingFlightIds, setCheckingFlightIds] = useState<number[]>([]);
    const [flightStatusMap, setFlightStatusMap] = useState<Record<number, FlightStatusDto>>({});

    const [driverLogs, setDriverLogs] = useState<
        Record<number, { statusId?: number; note?: string; createdAt?: string } | null>
    >({});

    const kindOptions = [
        { value: "1", label: "Arrival" },
        { value: "2", label: "Departure" },
        { value: "3", label: "Local" },
    ];

    // Matcher backend statusId
    const statusOptions = [
        { value: "3", label: "Confirmed" },
        { value: "1", label: "Booked" },
        { value: "4", label: "Cancelled" },
        { value: "2", label: "Delayed" },
    ];

    const filters = useMemo(
        () => ({
            kind: kind ? Number(kind) : null,
            statusId: status ? Number(status) : null,
            from: from ? from.toISOString().slice(0, 10) : null,
            to: to ? to.toISOString().slice(0, 10) : null,
            q: debouncedQ?.trim() ? debouncedQ.trim() : null,
        }),
        [kind, status, from, to, debouncedQ]
    );

    const driverFilterOptions = useMemo(() => {
        const seen = new Map<number, string>();

        bookings.forEach((b) => {
            if (b.driverId && b.driverName && !seen.has(b.driverId)) {
                seen.set(b.driverId, b.driverName);
            }
        });

        return Array.from(seen.entries())
            .map(([id, name]) => ({
                value: String(id),
                label: name,
            }))
            .sort((a, b) => a.label.localeCompare(b.label));
    }, [bookings]);

    const vehicleFilterOptions = useMemo(() => {
        return vehicles.map((v) => ({
            value: String(v.id),
            label: v.plate ? `${v.name} (${v.plate})` : v.name,
        }));
    }, [vehicles]);

    const handleSort = (field: string) => {
        if (sortBy === field) {
            setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
        } else {
            setSortBy(field);
            setSortDirection("asc");
        }
    };

    const renderSortableHeader = (label: string, field: string) => {
        const active = sortBy === field;
        const arrow = active ? (sortDirection === "asc" ? " ↑" : " ↓") : "";

        return (
            <span
                onClick={() => handleSort(field)}
                style={{ cursor: "pointer", userSelect: "none", fontWeight: 600 }}
            >
                {label}{arrow}
            </span>
        );
    };

    const filteredBookings = useMemo(() => {
        const search = debouncedQ.trim().toLowerCase();
        const fromMinutes = timeToMinutes(timeFrom);
        const toMinutes = timeToMinutes(timeTo);

        let result = bookings.filter((b) => {
            if (kind) {
                const bookingKind =
                    typeof b.kind === "number"
                        ? kindToString(b.kind)
                        : String(b.kind ?? "").toLowerCase();

                const selectedKind =
                    kind === "1" ? "arrival" :
                        kind === "2" ? "departure" :
                            kind === "3" ? "local" :
                                "";

                if (bookingKind !== selectedKind) return false;
            }

            if (status) {
                const bookingStatusId = String(b.statusTransportId ?? "");
                if (bookingStatusId !== status) return false;
            }

            const bookingDate = (b.scheduledDate ?? "").slice(0, 10);

            if (from) {
                const fromDate = from.toISOString().slice(0, 10);
                if (!bookingDate || bookingDate < fromDate) return false;
            }

            if (to) {
                const toDate = to.toISOString().slice(0, 10);
                if (!bookingDate || bookingDate > toDate) return false;
            }

            if (driverFilter && String(b.driverId ?? "") !== driverFilter) return false;
            if (vehicleFilter && String(b.vehicleId ?? "") !== vehicleFilter) return false;

            switch (planFilter) {
                case "unplanned":
                    if (b.driverId || b.vehicleId) return false;
                    break;
                case "hasDriver":
                    if (!b.driverId) return false;
                    break;
                case "hasVehicle":
                    if (!b.vehicleId) return false;
                    break;
                case "fullyPlanned":
                    if (!b.driverId || !b.vehicleId) return false;
                    break;
                case "missingDriver":
                    if (b.driverId) return false;
                    break;
                case "missingVehicle":
                    if (b.vehicleId) return false;
                    break;
                default:
                    break;
            }

            if (search) {
                const ft = fromToForRow(b);

                const bookingStatusLabel = b.status
                    ? statusMetaFromString(b.status).label
                    : statusMetaFromId(b.statusTransportId).label;

                const driverLog = driverLogs[b.transportRequestId];
                const driverStatusLabel = driverStatusMeta(driverLog?.statusId).label;

                const haystack = [
                    b.flightNo,
                    b.contactPerson,
                    b.contactPersonEmail,
                    b.pickUpPlace,
                    b.dropOffPlace,
                    b.driverName,
                    b.vehicleName,
                    b.vehiclePlate,
                    b.note,
                    b.whatsAppCountryCode,
                    b.whatsAppNumber,
                    b.scheduledDate,
                    ft.fromText,
                    ft.toText,
                    bookingStatusLabel,
                    driverStatusLabel,
                    driverLog?.note,
                    String(b.numberOfPersons ?? ""),
                    String((b as any).numberOfLuggage ?? ""),
                ]
                    .filter(Boolean)
                    .join(" ")
                    .toLowerCase();

                if (!haystack.includes(search)) return false;
            }

            if (timeFrom || timeTo) {
                const bookingMinutes = timeToMinutes(b.scheduledTime);

                if (bookingMinutes == null) return false;
                if (fromMinutes != null && bookingMinutes < fromMinutes) return false;
                if (toMinutes != null && bookingMinutes > toMinutes) return false;
            }

            return true;
        });

        if (sortBy) {
            result = [...result].sort((a, b) => {
                let aValue: any;
                let bValue: any;

                switch (sortBy) {
                    case "kind":
                        aValue = kindMeta(a.kind).label;
                        bValue = kindMeta(b.kind).label;
                        break;
                    case "date":
                        aValue = a.scheduledDate ?? "";
                        bValue = b.scheduledDate ?? "";
                        break;
                    case "time":
                        aValue = a.scheduledTime ?? "";
                        bValue = b.scheduledTime ?? "";
                        break;
                    case "fromTo":
                        aValue = `${fromToForRow(a).fromText} ${fromToForRow(a).toText}`;
                        bValue = `${fromToForRow(b).fromText} ${fromToForRow(b).toText}`;
                        break;
                    case "contact":
                        aValue = a.contactPerson ?? "";
                        bValue = b.contactPerson ?? "";
                        break;
                    case "status":
                        aValue = a.status
                            ? statusMetaFromString(a.status).label
                            : statusMetaFromId(a.statusTransportId).label;
                        bValue = b.status
                            ? statusMetaFromString(b.status).label
                            : statusMetaFromId(b.statusTransportId).label;
                        break;
                    case "driver":
                        aValue = a.driverName ?? "";
                        bValue = b.driverName ?? "";
                        break;
                    case "driverStatus":
                        aValue = (a as any).driverLogStatusText ?? (a as any).driverLogStatusId ?? 0;
                        bValue = (b as any).driverLogStatusText ?? (b as any).driverLogStatusId ?? 0;
                        break;
                    case "vehicle":
                        aValue = a.vehicleName ?? "";
                        bValue = b.vehicleName ?? "";
                        break;
                    case "pax":
                        aValue = a.numberOfPersons ?? 0;
                        bValue = b.numberOfPersons ?? 0;
                        break;
                    case "luggage":
                        aValue = (a as any).numberOfLuggage ?? 0;
                        bValue = (b as any).numberOfLuggage ?? 0;
                        break;
                    default:
                        aValue = "";
                        bValue = "";
                }

                const cmp = compareValues(aValue, bValue);
                return sortDirection === "asc" ? cmp : -cmp;
            });
        }

        return result;
    }, [
        bookings,
        driverLogs,
        kind,
        status,
        from,
        to,
        driverFilter,
        vehicleFilter,
        planFilter,
        debouncedQ,
        timeFrom,
        timeTo,
        sortBy,
        sortDirection,
    ]);

    const totalBookings = useMemo(() => filteredBookings.length, [filteredBookings]);

    const totalPassengers = useMemo(
        () => filteredBookings.reduce((sum, b) => sum + (b.numberOfPersons ?? 0), 0),
        [filteredBookings]
    );

    const totalPages = Math.max(1, Math.ceil(filteredBookings.length / PAGE_SIZE));

    const pagedBookings = useMemo(() => {
        const start = (page - 1) * PAGE_SIZE;
        return filteredBookings.slice(start, start + PAGE_SIZE);
    }, [filteredBookings, page]);

    useEffect(() => {
        setPage(1);
    }, [kind, status, from, to, q, driverFilter, vehicleFilter, planFilter, selectedTotalDate, sortBy, sortDirection]);

    useEffect(() => {
        if (page > totalPages) {
            setPage(totalPages);
        }
    }, [page, totalPages]);

    useEffect(() => {
        async function loadDriverLogs() {
            if (!bookings.length) {
                setDriverLogs({});
                return;
            }

            try {
                const entries = await Promise.all(
                    bookings.map(async (b) => {
                        const log = await getDriverLog(b.transportRequestId);
                        return [b.transportRequestId, log ?? null] as const;
                    })
                );

                setDriverLogs(Object.fromEntries(entries));
            } catch (err) {
                console.error("Kunne ikke hente driver logs", err);
            }
        }

        loadDriverLogs();
    }, [bookings]);

    const fetchAll = async (f: typeof filters, silent = false) => {
        if (!eventId || Number.isNaN(eventIdNum)) {
            setLoading(false);
            showNotification({
                color: "red",
                title: "Mangler eventId",
                message: "URL skal indeholde /:eventId",
            });
            return;
        }

        if (!silent) setLoading(true);

        try {
            const [b, t, v] = await Promise.all([
                getAdminBookings(eventIdNum, f),
                getAdminTotals(eventIdNum),
                getVehicles(eventIdNum),
            ]);

            setBookings(b);
            setTotals(t);
            setVehicles(v);
        } catch (e: any) {
            if (!silent) {
                showNotification({
                    color: "red",
                    title: "Fejl",
                    message: toErrorMessage(e),
                    autoClose: 8000,
                });
            }
        } finally {
            if (!silent) setLoading(false);
        }
    };

    useEffect(() => {
        if (!eventId || Number.isNaN(eventIdNum)) return;
        fetchAll(filters);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [eventIdNum, filters]);

    useEffect(() => {
        if (!eventId || Number.isNaN(eventIdNum) || messageDrawerOpen) return;

        bookingsRefreshRef.current = window.setInterval(() => {
            fetchAll(filters, true);
        }, 15000);

        return () => {
            if (bookingsRefreshRef.current) {
                window.clearInterval(bookingsRefreshRef.current);
                bookingsRefreshRef.current = null;
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [eventIdNum, filters, messageDrawerOpen]);

    const applyTotalDateFilter = (dateStr: string) => {
        const d = new Date(`${dateStr}T12:00:00`);
        setFrom(d);
        setTo(d);
        setSelectedTotalDate(dateStr);
    };

    const openPlanner = (row: AdminTransportBookingReadDto) => {
        setSelected(row);
        setDrawerOpen(true);
    };

    const openMessages = (row: AdminTransportBookingReadDto) => {
        setMessageBooking(row);
        setMessageDrawerOpen(true);
    };

    const CELL_BASE: React.CSSProperties = {
        borderTop: "1px solid rgba(0,0,0,0.06)",
        borderBottom: "1px solid rgba(0,0,0,0.06)",
        background: "white",
    };

    const handleCheckFlightStatus = async (b: AdminTransportBookingReadDto) => {
        if (!b.flightNo || !b.transportRequestId) return;

        setCheckingFlightIds((prev) => [...prev, b.transportRequestId]);

        try {
            const result = await checkFlightStatus(eventIdNum, b.transportRequestId);

            setFlightStatusMap((prev) => ({
                ...prev,
                [b.transportRequestId]: result,
            }));

            const isNotFound =
                String(result.status ?? "").toLowerCase().trim() === "not found";

            showNotification({
                color: isNotFound ? "gray" : "green",
                title: isNotFound ? "Ingen flight-data" : "Flight-status opdateret",
                message: result.message || `${result.flightNo}: ${result.status ?? "Ukendt status"}`,
            });
        } catch (e: any) {
            showNotification({
                color: "red",
                title: "Fejl ved flight-status",
                message: toErrorMessage(e),
            });
        } finally {
            setCheckingFlightIds((prev) =>
                prev.filter((id) => id !== b.transportRequestId)
            );
        }
    };

    const exportToExcel = () => {
        if (!filteredBookings.length) {
            showNotification({
                color: "yellow",
                title: "Ingen data",
                message: "Der er ingen bookings at eksportere.",
            });
            return;
        }

        const rows = filteredBookings.map((b) => {
            const km = kindMeta(b.kind);
            const sm = b.status
                ? statusMetaFromString(b.status)
                : statusMetaFromId(b.statusTransportId);

            //const dsm = driverStatusMeta(
            //    (b as any).driverLogStatusId,
            //    (b as any).driverLogStatusText
            //);

            const ft = fromToForRow(b);

            return {
                ID: b.transportRequestId,
                Type: km.label,
                Dato: b.scheduledDate ? b.scheduledDate.slice(0, 10) : "",
                Tid: b.scheduledTime ?? "",
                Fra: ft.fromText,
                Til: ft.toText,
                Kontaktperson: b.contactPerson ?? "",
                Email: b.contactPersonEmail ?? "",
                WhatsAppLandekode: b.whatsAppCountryCode ?? "",
                WhatsAppNummer: b.whatsAppNumber ?? "",
                Status: sm.label,
                DriverStatus: "",
                DriverStatusTid: (b as any).driverLogCreatedAt
                    ? new Date((b as any).driverLogCreatedAt).toLocaleString()
                    : "",
                DriverStatusNote: (b as any).driverLogNote ?? "",
                Passagerer: b.numberOfPersons ?? 0,
                Luggage: (b as any).numberOfLuggage ?? 0,
                FlightNo: b.flightNo ?? "",
                Driver: b.driverName ?? "",
                Vehicle: b.vehicleName ?? "",
                Nummerplade: b.vehiclePlate ?? "",
                PlanlagtPickup: b.plannedPickupAt
                    ? new Date(b.plannedPickupAt).toLocaleString()
                    : "",
                Note: b.note ?? "",
                UlæsteWhatsApp: b.unreadWhatsAppCount ?? 0,
            };
        });

        const worksheet = XLSX.utils.json_to_sheet(rows);
        const workbook = XLSX.utils.book_new();

        XLSX.utils.book_append_sheet(workbook, worksheet, "Bookings");

        const today = new Date().toISOString().slice(0, 10);
        const fileName = `transport-bookings-event-${eventIdNum}-${today}.xlsx`;

        XLSX.writeFile(workbook, fileName);

        showNotification({
            color: "green",
            title: "Eksporteret",
            message: `Excel-fil hentet som ${fileName}`,
        });
    };

    return (
        <>
            <Container size="xl" py="xl">
                <Stack gap="md">
                    <Paper withBorder radius="md" p="lg">
                        <Group justify="space-between" align="flex-start">
                            <Stack gap={2}>
                                <Title order={3}>Admin · Transport bookings</Title>
                                <Text c="dimmed" size="sm">Overblik + planlægning (driver/bil)</Text>
                            </Stack>

                            <Tooltip label="Opdater">
                                <ActionIcon
                                    variant="light"
                                    onClick={() => fetchAll(filters)}
                                    style={{ color: RED }}
                                >
                                    <IconRefresh size={18} />
                                </ActionIcon>
                            </Tooltip>
                        </Group>

                        <Divider my="sm" />

                        <Stack gap="md">
                            <Group align="flex-end" wrap="wrap" gap="sm">
                                <Select
                                    label="Kind"
                                    placeholder="Alle"
                                    data={kindOptions}
                                    value={kind}
                                    onChange={setKind}
                                    clearable
                                    style={{ width: 160 }}
                                />

                                <Select
                                    label="Status"
                                    placeholder="Alle"
                                    data={statusOptions}
                                    value={status}
                                    onChange={setStatus}
                                    clearable
                                    style={{ width: 160 }}
                                />

                                <DateInput
                                    label="From"
                                    placeholder="Dato fra"
                                    value={from}
                                    onChange={setFrom}
                                    clearable
                                    style={{ width: 150 }}
                                />

                                <DateInput
                                    label="To"
                                    placeholder="Dato til"
                                    value={to}
                                    onChange={setTo}
                                    clearable
                                    style={{ width: 150 }}
                                />

                                <TextInput
                                    label="Search"
                                    placeholder="Flight, navn, pickup, status..."
                                    value={q}
                                    onChange={(e) => setQ(e.currentTarget.value)}
                                    style={{ flex: 1, minWidth: 240 }}
                                />
                            </Group>

                            <Group align="flex-end" wrap="wrap" gap="sm">
                                <TextInput
                                    label="Time from"
                                    placeholder="09:00"
                                    value={timeFrom}
                                    onChange={(e) => setTimeFrom(e.currentTarget.value)}
                                    style={{ width: 110 }}
                                />

                                <TextInput
                                    label="Time to"
                                    placeholder="11:00"
                                    value={timeTo}
                                    onChange={(e) => setTimeTo(e.currentTarget.value)}
                                    style={{ width: 110 }}
                                />

                                <Select
                                    label="Driver"
                                    placeholder="Alle drivers"
                                    data={driverFilterOptions}
                                    value={driverFilter}
                                    onChange={setDriverFilter}
                                    clearable
                                    searchable
                                    style={{ width: 210 }}
                                />

                                <Select
                                    label="Vehicle"
                                    placeholder="Alle biler"
                                    data={vehicleFilterOptions}
                                    value={vehicleFilter}
                                    onChange={setVehicleFilter}
                                    clearable
                                    searchable
                                    style={{ width: 210 }}
                                />

                                <Button
                                    onClick={() => fetchAll(filters)}
                                    style={{ backgroundColor: RED }}
                                >
                                    Filtrér
                                </Button>

                                <Button
                                    variant="default"
                                    style={{ minWidth: 120 }}
                                    onClick={() => {
                                        setKind(null);
                                        setStatus(null);
                                        setFrom(null);
                                        setTo(null);
                                        setTimeFrom("");
                                        setTimeTo("");
                                        setQ("");
                                        setDriverFilter(null);
                                        setVehicleFilter(null);
                                        setSelectedTotalDate(null);
                                        setPlanFilter("all");
                                        setSortBy(null);
                                        setSortDirection("asc");
                                    }}
                                >
                                    Nulstil filtre
                                </Button>

                                <Button
                                    variant="light"
                                    color="green"
                                    leftSection={<IconFileSpreadsheet size={16} />}
                                    onClick={exportToExcel}
                                >
                                    Eksportér Excel
                                </Button>

                                <Group ml="auto" gap="xs">
                                    <Badge variant="light" color="red" size="lg">
                                        {totalBookings} bookings
                                    </Badge>
                                    <Badge variant="light" color="red" size="lg">
                                        {totalPassengers} pax
                                    </Badge>
                                </Group>
                            </Group>
                        </Stack>

                        <Group gap="xs" mt="sm" wrap="wrap">
                            <Button
                                size="xs"
                                variant={planFilter === "all" ? "filled" : "light"}
                                color="gray"
                                onClick={() => setPlanFilter("all")}
                            >
                                Alle
                            </Button>

                            <Button
                                size="xs"
                                variant={planFilter === "unplanned" ? "filled" : "light"}
                                color="red"
                                onClick={() => setPlanFilter("unplanned")}
                            >
                                Ikke planlagt
                            </Button>

                            <Button
                                size="xs"
                                variant={planFilter === "hasDriver" ? "filled" : "light"}
                                color="green"
                                onClick={() => setPlanFilter("hasDriver")}
                            >
                                Har driver
                            </Button>

                            <Button
                                size="xs"
                                variant={planFilter === "hasVehicle" ? "filled" : "light"}
                                color="blue"
                                onClick={() => setPlanFilter("hasVehicle")}
                            >
                                Har bil
                            </Button>

                            <Button
                                size="xs"
                                variant={planFilter === "fullyPlanned" ? "filled" : "light"}
                                color="teal"
                                onClick={() => setPlanFilter("fullyPlanned")}
                            >
                                Fuldt planlagt
                            </Button>

                            <Button
                                size="xs"
                                variant={planFilter === "missingDriver" ? "filled" : "light"}
                                color="orange"
                                onClick={() => setPlanFilter("missingDriver")}
                            >
                                Mangler driver
                            </Button>

                            <Button
                                size="xs"
                                variant={planFilter === "missingVehicle" ? "filled" : "light"}
                                color="yellow"
                                onClick={() => setPlanFilter("missingVehicle")}
                            >
                                Mangler bil
                            </Button>
                        </Group>
                    </Paper>

                    <Paper withBorder radius="md" p="lg">
                        <Group
                            justify="space-between"
                            mb="md"
                            style={{ cursor: "pointer" }}
                            onClick={() => setTotalsOpen((o) => !o)}
                        >
                            <Group gap="xs">
                                <Title order={5}>Totals (confirmed) pr dag</Title>
                                <Badge variant="light">{totals.length}</Badge>
                            </Group>

                            <Text size="sm" c="dimmed">
                                {totalsOpen ? "Skjul" : "Vis"}
                            </Text>
                        </Group>

                        <Collapse in={totalsOpen}>
                            {totals.length === 0 ? (
                                <Text c="dimmed" size="sm">Ingen totals endnu.</Text>
                            ) : (
                                <Group gap="md" wrap="wrap">
                                    {totals.map((t) => {
                                        const active = selectedTotalDate === t.date;
                                        const busy = t.bookings >= 3 || t.passengers >= 6;

                                        return (
                                            <Paper
                                                key={t.date}
                                                withBorder
                                                radius="md"
                                                p="md"
                                                onClick={() => applyTotalDateFilter(t.date)}
                                                style={{
                                                    cursor: "pointer",
                                                    width: 180,
                                                    transition: "all 140ms ease",
                                                    borderColor: active ? RED : "rgba(0,0,0,0.08)",
                                                    backgroundColor: active ? "rgba(255,94,94,0.06)" : "white",
                                                    boxShadow: active
                                                        ? "0 8px 20px rgba(255,94,94,0.12)"
                                                        : "0 2px 8px rgba(0,0,0,0.04)",
                                                }}
                                            >
                                                <Stack gap={8}>
                                                    <Text size="sm" fw={700} c={active ? "red" : undefined}>
                                                        {t.date}
                                                    </Text>

                                                    <Group gap="xs">
                                                        <Badge
                                                            variant={active ? "filled" : "light"}
                                                            color={busy ? "red" : "gray"}
                                                        >
                                                            {t.bookings} bookings
                                                        </Badge>

                                                        <Badge
                                                            variant={active ? "filled" : "light"}
                                                            color={busy ? "red" : "gray"}
                                                        >
                                                            {t.passengers} pax
                                                        </Badge>
                                                    </Group>
                                                </Stack>
                                            </Paper>
                                        );
                                    })}
                                </Group>
                            )}
                        </Collapse>

                        {selectedTotalDate && (
                            <Group mt="md" gap="xs">
                                <Badge color="red" variant="filled">
                                    Filter: {selectedTotalDate}
                                </Badge>

                                <Button
                                    size="xs"
                                    variant="subtle"
                                    color="gray"
                                    onClick={() => {
                                        setFrom(null);
                                        setTo(null);
                                        setSelectedTotalDate(null);
                                    }}
                                >
                                    Ryd dato-filter
                                </Button>
                            </Group>
                        )}
                    </Paper>
                </Stack>
            </Container>

            <div style={{ width: "100%", padding: "0 24px 24px" }}>
                <Paper withBorder radius="md" p="lg">
                    <Group justify="space-between" mb="sm">
                        <Title order={4}>Bookings</Title>
                        {loading && <Loader size="sm" />}
                    </Group>

                    {loading ? (
                        <Center py="xl">
                            <Loader />
                        </Center>
                    ) : filteredBookings.length === 0 ? (
                        <Text c="dimmed">Ingen bookings matcher dine filtre.</Text>
                    ) : (
                        <>
                            <ScrollArea>
                                <Table
                                    highlightOnHover
                                    verticalSpacing="sm"
                                    horizontalSpacing="md"
                                    withRowBorders={false}
                                    style={{
                                        borderCollapse: "separate",
                                        borderSpacing: "0 10px",
                                        minWidth: 1780,
                                        width: "100%",
                                        tableLayout: "auto",
                                    }}
                                >
                                    <Table.Thead>
                                        <Table.Tr>
                                            <Table.Th>{renderSortableHeader("Kind", "kind")}</Table.Th>
                                            <Table.Th>{renderSortableHeader("Date", "date")}</Table.Th>
                                            <Table.Th>{renderSortableHeader("Time", "time")}</Table.Th>
                                            <Table.Th>{renderSortableHeader("From → To", "fromTo")}</Table.Th>
                                            <Table.Th>{renderSortableHeader("Contact", "contact")}</Table.Th>
                                            <Table.Th style={{ minWidth: 150 }}>
                                                {renderSortableHeader("Status", "status")}
                                            </Table.Th>
                                            <Table.Th>Messages</Table.Th>
                                            <Table.Th>{renderSortableHeader("Plan", "driver")}</Table.Th>
                                            <Table.Th style={{ minWidth: 170 }}>
                                                {renderSortableHeader("Driver status", "driverStatus")}
                                            </Table.Th>
                                            <Table.Th style={{ minWidth: 90, textAlign: "center" }}>
                                                {renderSortableHeader("Pax", "pax")}
                                            </Table.Th>
                                            <Table.Th style={{ minWidth: 110, textAlign: "center" }}>
                                                {renderSortableHeader("Luggage", "luggage")}
                                            </Table.Th>
                                            <Table.Th style={{ minWidth: 100 }}>Flight</Table.Th>
                                            <Table.Th style={{ width: 30, textAlign: "right" }} />
                                        </Table.Tr>
                                    </Table.Thead>

                                    <Table.Tbody>
                                                {pagedBookings.map((b) => {
                                                    const driverLog = driverLogs[b.transportRequestId];
                                                    const driverStatus = driverStatusMeta(driverLog?.statusId);
                                            const km = kindMeta(b.kind);
                                            const sm = b.status
                                                ? statusMetaFromString(b.status)
                                                : statusMetaFromId(b.statusTransportId);

                                            //const dsm = driverStatusMeta(
                                            //    (b as any).driverLogStatusId,
                                            //    (b as any).driverLogStatusText
                                            //);

                                            const ft = fromToForRow(b);
                                            const notePreview = previewText(b.note, 50);
                                            const hasNote = !!(b.note && b.note.trim().length > 0);

                                            const driverLogNote = (b as any).driverLogNote;
                                            const driverLogCreatedAt = (b as any).driverLogCreatedAt;

                                            const firstCellStyle: React.CSSProperties = {
                                                ...CELL_BASE,
                                                borderLeft: "1px solid rgba(0,0,0,0.06)",
                                                borderTopLeftRadius: 10,
                                                borderBottomLeftRadius: 10,
                                                paddingLeft: 14,
                                            };

                                            const middleCellStyle: React.CSSProperties = {
                                                ...CELL_BASE,
                                            };

                                            const lastCellStyle: React.CSSProperties = {
                                                ...CELL_BASE,
                                                borderRight: "1px solid rgba(0,0,0,0.06)",
                                                borderTopRightRadius: 10,
                                                borderBottomRightRadius: 10,
                                                textAlign: "right",
                                                paddingRight: 10,
                                            };

                                            const liveFlightStatus = flightStatusMap[b.transportRequestId];
                                            const checkingFlight = checkingFlightIds.includes(b.transportRequestId);

                                            return (
                                                <Table.Tr key={b.transportRequestId}>
                                                    <Table.Td style={firstCellStyle}>
                                                        <Group gap={6} wrap="nowrap">
                                                            {km.icon}
                                                            <Text size="sm" fw={700}>{km.label}</Text>
                                                        </Group>
                                                        {b.flightNo ? (
                                                            <Text size="xs" c="dimmed">
                                                                Flight: {b.flightNo}
                                                            </Text>
                                                        ) : null}
                                                    </Table.Td>

                                                    <Table.Td style={middleCellStyle}>
                                                        <Text size="sm">
                                                            {b.scheduledDate ? b.scheduledDate.slice(0, 10) : "-"}
                                                        </Text>
                                                    </Table.Td>

                                                    <Table.Td style={middleCellStyle}>
                                                        <Text size="sm">{b.scheduledTime ?? "-"}</Text>
                                                        {b.plannedPickupAt ? (
                                                            <Text size="xs" c="dimmed">
                                                                Plan: {new Date(b.plannedPickupAt).toLocaleString()}
                                                            </Text>
                                                        ) : null}
                                                    </Table.Td>

                                                    <Table.Td style={middleCellStyle}>
                                                        <Text size="sm" fw={700}>{ft.fromText}</Text>
                                                        <Text size="xs" c="dimmed">→ {ft.toText}</Text>
                                                    </Table.Td>

                                                    <Table.Td style={middleCellStyle}>
                                                        <Text size="sm" fw={700}>{b.contactPerson ?? "-"}</Text>

                                                        {b.contactPersonEmail ? (
                                                            <Text size="xs" c="dimmed">
                                                                Email: {b.contactPersonEmail}
                                                            </Text>
                                                        ) : null}

                                                        <Text size="xs" c="dimmed">
                                                            WhatsApp: {b.whatsAppCountryCode ?? ""} {b.whatsAppNumber ?? ""}
                                                        </Text>
                                                    </Table.Td>

                                                    <Table.Td style={middleCellStyle}>
                                                        <Badge variant="light" color={sm.color} leftSection={sm.icon}>
                                                            {sm.label}
                                                        </Badge>
                                                    </Table.Td>

                                                    <Table.Td style={middleCellStyle}>
                                                        <Group gap="xs" wrap="wrap">
                                                            <Button
                                                                size="xs"
                                                                variant="light"
                                                                color="green"
                                                                leftSection={<IconBrandWhatsapp size={14} />}
                                                                onClick={() => openMessages(b)}
                                                            >
                                                                Chat
                                                            </Button>

                                                            {(b.unreadWhatsAppCount ?? 0) > 0 && (
                                                                <Badge color="red" variant="filled">
                                                                    {b.unreadWhatsAppCount}
                                                                </Badge>
                                                            )}
                                                        </Group>
                                                    </Table.Td>

                                                    <Table.Td style={middleCellStyle}>
                                                        <Group gap="xs" wrap="wrap">
                                                            <Badge
                                                                variant="light"
                                                                color={b.driverName ? "green" : "gray"}
                                                                style={{ cursor: b.driverId ? "pointer" : "default" }}
                                                                onClick={() => {
                                                                    if (b.driverId) setDriverFilter(String(b.driverId));
                                                                }}
                                                            >
                                                                {b.driverName ?? "No driver"}
                                                            </Badge>

                                                            <Badge
                                                                variant="light"
                                                                color={b.vehicleName ? "green" : "gray"}
                                                                style={{ cursor: b.vehicleId ? "pointer" : "default" }}
                                                                onClick={() => {
                                                                    if (b.vehicleId) setVehicleFilter(String(b.vehicleId));
                                                                }}
                                                            >
                                                                {b.vehicleName
                                                                    ? `${b.vehicleName} (${b.vehiclePlate ?? "-"})`
                                                                    : "No vehicle"}
                                                            </Badge>

                                                            {hasNote && (
                                                                <Tooltip label={b.note ?? ""} multiline w={360}>
                                                                    <Badge
                                                                        variant="light"
                                                                        color="blue"
                                                                        leftSection={<IconNote size={14} />}
                                                                    >
                                                                        {notePreview}
                                                                    </Badge>
                                                                </Tooltip>
                                                            )}
                                                        </Group>

                                                        {!hasNote ? (
                                                            <Text size="xs" c="dimmed" mt={4}>No note</Text>
                                                        ) : null}
                                                    </Table.Td>

                                                    <Table.Td style={middleCellStyle}>
                                                        <Stack gap={4}>
                                                            <Badge variant="light" color={driverStatus.color}>
                                                                {driverStatus.label}
                                                            </Badge>

                                                            {driverLog?.createdAt ? (
                                                                <Text size="xs" c="dimmed">
                                                                    {new Date(driverLog.createdAt).toLocaleString()}
                                                                </Text>
                                                            ) : (
                                                                <Text size="xs" c="dimmed">
                                                                    Ingen log endnu
                                                                </Text>
                                                            )}

                                                            {driverLog?.note ? (
                                                                <Tooltip label={driverLog.note} multiline w={320}>
                                                                    <Badge
                                                                        variant="light"
                                                                        color="blue"
                                                                        leftSection={<IconNote size={12} />}
                                                                        style={{ maxWidth: 170 }}
                                                                    >
                                                                        {previewText(driverLog.note, 24)}
                                                                    </Badge>
                                                                </Tooltip>
                                                            ) : (
                                                                <Text size="xs" c="dimmed">No driver note</Text>
                                                            )}
                                                        </Stack>
                                                    </Table.Td>

                                                    <Table.Td style={{ ...middleCellStyle, textAlign: "center", minWidth: 90 }}>
                                                        <Badge
                                                            variant="light"
                                                            color="red"
                                                            style={{
                                                                minWidth: 56,
                                                                display: "inline-flex",
                                                                justifyContent: "center",
                                                            }}
                                                        >
                                                            {b.numberOfPersons ?? 0}
                                                        </Badge>
                                                    </Table.Td>

                                                    <Table.Td style={{ ...middleCellStyle, textAlign: "center", minWidth: 110 }}>
                                                        <Badge
                                                            variant="light"
                                                            color="yellow"
                                                            style={{
                                                                minWidth: 56,
                                                                display: "inline-flex",
                                                                justifyContent: "center",
                                                            }}
                                                        >
                                                            {(b as any).numberOfLuggage ?? 0}
                                                        </Badge>
                                                    </Table.Td>

                                                    <Table.Td style={middleCellStyle}>
                                                        <Group gap="xs" wrap="nowrap">
                                                            {flightStatusBadge(b, liveFlightStatus)}

                                                            <Tooltip label="Check flight status">
                                                                <ActionIcon
                                                                    variant="light"
                                                                    disabled={!b.flightNo || checkingFlight}
                                                                    loading={checkingFlight}
                                                                    onClick={() => handleCheckFlightStatus(b)}
                                                                    style={{ color: RED }}
                                                                >
                                                                    <IconRefresh size={16} />
                                                                </ActionIcon>
                                                            </Tooltip>
                                                        </Group>
                                                    </Table.Td>

                                                    <Table.Td style={lastCellStyle}>
                                                        <Tooltip label="Planlæg (driver/bil)">
                                                            <ActionIcon
                                                                variant="light"
                                                                onClick={() => openPlanner(b)}
                                                                style={{ color: RED }}
                                                            >
                                                                <IconSettings size={18} />
                                                            </ActionIcon>
                                                        </Tooltip>
                                                    </Table.Td>
                                                </Table.Tr>
                                            );
                                        })}
                                    </Table.Tbody>
                                </Table>
                            </ScrollArea>

                            <Group justify="space-between" mt="md">
                                <Text size="sm" c="dimmed">
                                    Viser {(page - 1) * PAGE_SIZE + 1} - {Math.min(page * PAGE_SIZE, filteredBookings.length)} af {filteredBookings.length} bookings
                                </Text>

                                <Pagination
                                    value={page}
                                    onChange={setPage}
                                    total={totalPages}
                                    color="red"
                                />
                            </Group>
                        </>
                    )}
                </Paper>
            </div>

            <AssignBookingDrawer
                opened={drawerOpen}
                onClose={() => {
                    setDrawerOpen(false);
                    fetchAll(filters, true);
                }}
                bookingId={selected?.transportRequestId ?? null}
                eventId={eventIdNum}
                drivers={drivers}
                vehicles={vehicles}
                initial={{
                    kind: selected?.kind ?? null,
                    driverId: selected?.driverId ?? null,
                    vehicleId: selected?.vehicleId ?? null,
                    plannerNote: selected?.note ?? null,
                    statusTransportId: selected?.statusTransportId ?? null,
                    scheduledDate: selected?.scheduledDate ?? null,
                    scheduledTime: selected?.scheduledTime ?? null,
                    pickUpPlace: selected?.pickUpPlace ?? null,
                    dropOffPlace: selected?.dropOffPlace ?? null,
                }}
                onSaved={() => fetchAll(filters, true)}
            />

            <TransportMessageDrawer
                opened={messageDrawerOpen}
                onClose={() => {
                    setMessageDrawerOpen(false);
                    fetchAll(filters, true);
                }}
                bookingId={messageBooking?.transportRequestId ?? null}
                contactName={messageBooking?.contactPerson ?? null}
                phoneNumber={`${messageBooking?.whatsAppCountryCode ?? ""} ${messageBooking?.whatsAppNumber ?? ""}`.trim()}
            />
        </>
    );
}